/**
 * ProjectsPage Component
 */

import * as playerActions from '../../../redux/actions/player';
import {
  getLicenseString,
  licenseUsagesToSearchParams,
  setLicenseFromParams,
} from '../../../redux/helpers/license';
import { getPlayerTracksFromResults } from '../../../redux/helpers/player';
import { getLicenseComponents } from '../../../redux/selectors/staticData';
import {
  ProjectPageFragment,
  UpdateProjectMutation,
} from '../../../relay/project';
import { processMutation } from '../../../utils/relay';
import { linkWithCurrentQueryString } from '../../../utils/router';
import { baseline } from '../../styles/grid';
import {
  generateActiveProjectItemProps,
  generateProjectItemsProps,
  IFormFields,
  ProjectsPageReduxForm,
  reinitializeForm,
} from './form';
import './styles.scss';

import * as classNames from 'classnames';
import { RouteMatch } from 'found';
import { isEqual } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect } from 'react-redux';
import { createFragmentContainer } from 'react-relay';
import { FormProps } from 'redux-form';
import { VelocityTransitionGroup } from 'velocity-react';

import Page from '../../../containers/Page';
import Button from '../../atoms/buttons/Button';
import LicenseBuilderButton from '../../atoms/buttons/LicenseBuilderButton';
import NProgress from '../../atoms/feedback/NProgress';
import { IconDownload, IconSettings, IconTerms, IconX } from '../../atoms/images/Icon';
import Heading from '../../atoms/text/Heading';
import { Span } from '../../atoms/text/Label';
import { Tooltip } from '../../atoms/text/Tooltip';
import PageHeader from '../../molecules/blocks/PageHeader';
import CurrencySelect from '../../molecules/forms/Select/CurrencySelect';
import { getTextColorClassName } from '../../styles/colors';
import NotAuthorizedPage from '../account/NotAuthorized';
import CreateProjectForm from './CreateProjectForm';
import ProjectList from './ProjectList';
import ProjectSettings from './ProjectSettings';
import ProjectView from './ProjectView';

// Page Path
export const ProjectsPagePath = '/my/playlists';
export const ProjectPagePath = (id: string) => `/my/playlists/${id}`;

// Page Props
interface IOwnStateProps extends Pick<ProjectsPageProps, 'initialValues'> {
  activeProjectExists: boolean;
  licenseComponents: GQL.ILicenseComponents;
  userIsLoggedIn: boolean;
  userProfile: GQL.IProfile | undefined;
}

interface IOwnDispatchProps {
  setPlayerQueue(tracks: IPlayerTrack[]): void;
}

interface IOwnMergedProps {
  reinitializeForm(formFields: IFormFields): void;
}

interface IOwnProps extends RouteMatch {
  viewer: GQL.IViewer;
}

type ProjectsPageBaseProps = IOwnProps & FormProps<IFormFields, IOwnProps, any>;
export type ProjectsPageProps =
  & ProjectsPageBaseProps
  & IOwnStateProps
  & IOwnDispatchProps
  & IOwnMergedProps;

interface IOwnState {
  activeProjectId: string;
  activeProjectLicense: ILicenseUsage[];
  status: 'fetch' | 'create' | 'delete' | null;
  isSettingsPanelOpen: boolean;
  redirecting: boolean;
}

const classes = new BEM('projects');
const defaultList = [];

function licenseParamsToUsages({ licenseComponents, viewer }: ProjectsPageProps) {
  if (viewer.node) {
    return setLicenseFromParams(licenseComponents, (viewer.node as GQL.IProject).license_params || defaultList);
  }

  return defaultList;
}

class ProjectsPage extends React.Component<ProjectsPageProps, IOwnState> {

  static getDerivedStateFromProps(nextProps: ProjectsPageProps, prevState: IOwnState) {
    if (nextProps.routeParams.id !== prevState.activeProjectId) {
      return {
        activeProjectId: nextProps.routeParams.id,
        activeProjectLicense: licenseParamsToUsages(nextProps),
      };
    }

    return null;
  }

  constructor(props: ProjectsPageProps) {
    super(props);

    const activeProject = props.viewer.node as GQL.IProject;
    let status = false;
    let redirecting = false;

    if (!props.routeParams.id) {
      if (props.viewer.latest_project) {
        redirecting = true;
        const projectPath = ProjectPagePath(props.viewer.latest_project.id);
        props.router.replace(linkWithCurrentQueryString(projectPath));
      }
    } else {
      if (activeProject) {
        this.initActiveProject(activeProject, false);
        status = true;
      }
    }

    this.state = {
      activeProjectId: props.routeParams.id || '',
      activeProjectLicense: licenseParamsToUsages(this.props),
      status: status ? 'fetch' : null,
      isSettingsPanelOpen: true,
      redirecting,
    };
  }

  componentDidMount() {
    if (this.state.status) {
      this.setState({ status: null });
    }
  }

  componentDidUpdate(prevProps: ProjectsPageProps, prevState: IOwnState) {
    if (this.hasSwitchedProject(prevProps)) {
      this.setState({ redirecting: false, status: null }, () => {
        this.initActiveProject();
      });
    } else if (this.hasSharedProjectViaSidebar(prevProps)) {
      this.onToggleSettingsPanel();
    } else if (this.hasDeletedLastProject(prevProps)) {
      this.redirect(ProjectsPagePath, true);
    } else if (this.hasDeletedProject(prevProps)) {
      this.redirect(ProjectPagePath(this.firstProjectInList().id));
    } else if (this.hasCreatedNewProject(prevProps)) {
      this.redirect(ProjectPagePath(this.firstProjectInList().id));
    } else if (this.hasDeletedTrackInProject(prevProps)) {
      this.initActiveProject();
    }
  }

  onSelectProject = (activeProjectId: string) => {
    if (activeProjectId !== this.state.activeProjectId) {
      this.setState({ status: 'fetch' });
    }
  }

  onToggleSettingsPanel = () => {
    this.setState({ isSettingsPanelOpen: !this.state.isSettingsPanelOpen });
  }

  closeSettingsPanel = () => {
    this.setState({ isSettingsPanelOpen: false });
  }

  onCreateProject = () => {
    this.setState({ status: 'create' });
  }

  onDeleteProject = () => {
    this.setState({ status: 'delete' });
  }

  onSaveLicense = (usages: ILicenseUsage[]) => {
    this.setState({ activeProjectLicense: usages }, async () => {
      const { license_params } = this.getProjectNode();
      const licenseParams = licenseUsagesToSearchParams(usages);
      if (!isEqual(license_params, licenseParams)) {
        await processMutation<GQL.IUpdateProjectInput>(UpdateProjectMutation, {
          id: this.state.activeProjectId,
          license_params: licenseParams,
        });
      }
    });
  }

  renderHeader() {
    const {
      activeProjectLicense,
      status,
      isSettingsPanelOpen,
    } = this.state;

    const {
      activeProjectExists,
      submitting,
    } = this.props;

    const projectHasLicense = activeProjectLicense.length > 0;

    let componentToRender: JSX.Element | null;

    if (status || submitting) {
      let label: string;
      switch (status) {
        case 'fetch':
          label = 'Loading playlist...';
          break;
        case 'create':
          label = 'Creating playlist...';
          break;
        case 'delete':
          label = 'Deleting playlist...';
          break;
        default:
          label = 'Saving playlist...';
          break;
      }
      componentToRender = (
        <Span size="large" variant="description" textColor="bw6" style={{ marginRight: baseline }}>{label}</Span>
      );
    } else if (projectHasLicense) {
      const licenseTerms = getLicenseString(activeProjectLicense);
      componentToRender = (
        <>
          <IconTerms className={getTextColorClassName('bw6')} />
          <Tooltip
            content={licenseTerms}
            size="small"
            theme="black"
            placement="bottom-start"
            delay={[250, 0]}
            arrow={false}
            animateFill={false}
          >
            <LicenseBuilderButton
              label={(
                <Span size="large" variant="description" {...classes('license__string')}>
                  {licenseTerms}
               </Span>
              )}
              title="Edit License Terms For This Playlist"
              variant="text"
              size="large"
              usages={this.state.activeProjectLicense}
              onSaveLicense={this.onSaveLicense}
              scope={{
                type: 'Project',
                id: this.state.activeProjectId,
              }}
            />
          </Tooltip>
        </>
      );
    } else if (activeProjectExists) {
      componentToRender = (
        <LicenseBuilderButton
          label="Show Prices For This Playlist"
          colorScheme="accent"
          variant="pill"
          size="xsmall"
          icon={<IconTerms />}
          scope={{
            type: 'Project',
            id: this.state.activeProjectId,
          }}
          onSaveLicense={this.onSaveLicense}
          withShadow
        />
      );
    } else {
      componentToRender = null;
    }

    const project = this.getProjectNode();

    return (
      <>
        {/* FIXME: this zIndex wrapper is a quick fix for a CSS z-index kerfuffle that ends up
        resetting the z-index value defined in Projects/styles.scss.*/}
        <div style={{zIndex: 3}}>
          <PageHeader title="My Playlists" {...classes('header')}>
            <div {...classes('header__sidebar')}>
                <CreateProjectForm onCreateProject={this.onCreateProject} />
            </div>
            <div {...classes('header__project')}>
              <VelocityTransitionGroup
                component="div"
                enter={{ animation: 'transition.slideUpIn', delay: 150, display: 'flex', duration: 150 }}
                leave={{ animation: 'transition.slideUpOut', duration: 150 }}
                {...classes('license')}
              >{componentToRender}
              </VelocityTransitionGroup>

              {activeProjectExists && (
                <div {...classes('header__project__actions') }>
                  {!!project.tracks && !!project.tracks.edges!.length && (
                    <Button
                      wrapperComponent="a"
                      hyperLinkProps={{
                        href: `/projects/${this.state.activeProjectId}/download`,
                        target: '_blank',
                      }}
                      label="Download Tracks"
                      colorScheme="transparent"
                      icon={<IconDownload />}
                      disabled={this.isWorking}
                    />
                  )}
                  <Button
                    label="Settings"
                    colorScheme="transparent"
                    icon={isSettingsPanelOpen ? <IconX /> : <IconSettings />}
                    onClick={this.onToggleSettingsPanel}
                    disabled={this.isWorking}
                    {...classes('project-settings-button', { open: isSettingsPanelOpen })}
                  />
                {projectHasLicense && <CurrencySelect />}
                </div>
              )}
            </div>
          </PageHeader>
        </div>
        <NProgress active={this.isWorking} {...classes('loading-progress')} />
      </>
    );
  }

  render() {
    const {
      isSettingsPanelOpen,
      redirecting,
      status,
    } = this.state;

    const {
      activeProjectExists,
      handleSubmit,
      userIsLoggedIn,
    } = this.props;

    const projects = this.getProjects();
    const projectNode = this.getProjectNode();

    const extraClasses = classNames(
      isSettingsPanelOpen && 'settings-open',
      !userIsLoggedIn && 'not-authorized',
      userIsLoggedIn && (!activeProjectExists || !projects.length) && 'project-not-found',
    );

    if (!userIsLoggedIn) {
      return <NotAuthorizedPage message="You must be logged in to access your projects." />;
    }

    return (
      <Page id="projects" className={extraClasses} noIndex>
        {this.renderHeader()}
        <form {...classes('form') } onSubmit={handleSubmit}>
          {!!projects.length ? (
            <>
            <ProjectList onSelectProject={this.onSelectProject} onDeleteProject={this.onDeleteProject} />
            {activeProjectExists ? (
              <>
              <ProjectView
                project={projectNode}
                license={this.state.activeProjectLicense}
                onSaveLicense={this.onSaveLicense}
              />
              <ProjectSettings
                submitting={!!this.props.submitting}
                project={projectNode}
                open={isSettingsPanelOpen}
                license={this.state.activeProjectLicense}
                onSaveLicense={this.onSaveLicense}
                onRequestClose={this.closeSettingsPanel}
              />
              </>
            ) : !status && !redirecting ? (
              <Heading is="h2" textColor="bw6" {...classes('error-message', 'not-found') }>Project not found.</Heading>
            ) : null}
            </>
          ) : (
            <Heading
              is="h2"
              textColor="bw6"
              {...classes('error-message', 'no-project') }
            >You don't have any projects.
            </Heading>
          )}
        </form>
      </Page>
    );
  }

  get isWorking() {
    return !!this.state.status || !!this.props.submitting;
  }

  private initActiveProject(project = this.getProjectNode(), reinitForm = true) {
    if (reinitForm) {
      this.props.reinitializeForm({
        activeProject: generateActiveProjectItemProps(project),
        projects: generateProjectItemsProps(this.getProjects(), this.props.userProfile),
      });
    }

    const newTracks = getPlayerTracksFromResults(project.tracks!.edges!.map(({ node }) => node!));
    this.props.setPlayerQueue(newTracks);
  }

  private getProjectNode(props = this.props) {
    return props.viewer.node as GQL.IProject;
  }

  private edges(props = this.props) {
    return props.viewer.projects ? props.viewer.projects.edges! : defaultList;
  }

  private firstProjectInList(props = this.props) {
    return this.edges(props)[0].node!;
  }

  private getProjects(props = this.props) {
    return this.edges(props).map(({ node }) => node!);
  }

  private hasDeletedTrackInProject(props: ProjectsPageProps) {
    const currentActiveProject = this.getProjectNode();
    const project = this.getProjectNode(props);

    if (currentActiveProject && project) {
      return this.state.activeProjectId && currentActiveProject.tracks!.edges!.length !== project.tracks!.edges!.length;
    }

    return false;
  }

  private hasSwitchedProject(props: ProjectsPageProps) {
    const currentActiveProject = this.getProjectNode();
    const project = this.getProjectNode(props);

    return currentActiveProject && (!!!project || currentActiveProject.id !== project.id);
  }

  private hasDeletedLastProject(prevProps: ProjectsPageProps) {
    return this.edges(prevProps).length === 1 && this.edges().length === 0;
  }

  private hasCreatedNewProject(prevProps: ProjectsPageProps) {
    return this.edges(prevProps).length < this.edges().length;
  }

  private hasDeletedProject(prevProps: ProjectsPageProps) {
    return this.edges(prevProps).length > this.edges().length;
  }

  private hasSharedProjectViaSidebar(prevProps: ProjectsPageProps) {
    const currentActiveProject = this.getProjectNode();
    const previousActiveProject = this.getProjectNode(prevProps);
    return currentActiveProject && previousActiveProject
      && !this.hasSwitchedProject(prevProps)
      && currentActiveProject.is_public && !previousActiveProject.is_public
      && !this.state.isSettingsPanelOpen;
  }

  private redirect(path: string, replace: boolean = false) {
    const redirectDelay = 500; // Delay to let the progress bar animation complete
    const action = replace ? this.props.router.replace : this.props.router.push;
    this.setState({ status: null, redirecting: true }, () => setTimeout(() => action(path), redirectDelay));
  }
}

const ProjectsPageForm = ProjectsPageReduxForm(ProjectsPage);

// tslint:disable-next-line:max-line-length
const ConnectedProjectsPageForm = connect<IOwnStateProps, IOwnDispatchProps & IDispatchProps, ProjectsPageBaseProps, ProjectsPageProps, IStoreState>(
  (state, ownProps) => {
    const {
      user: { isLoggedIn, profile },
    } = state;

    let projects: GQL.IProject[] = [];

    const defaultProject = ownProps.routeParams.id && ownProps.viewer.node ?
      ownProps.viewer.node as GQL.IProject
      : ownProps.viewer.latest_project!;

    if (isLoggedIn) {
      projects = ownProps.viewer.projects!.edges!.map(({ node }) => node!);
    }

    return {
      licenseComponents: getLicenseComponents(state),
      initialValues: defaultProject ? {
        activeProject: generateActiveProjectItemProps(defaultProject),
        projects: generateProjectItemsProps(projects, profile),
      } : undefined,
      activeProjectExists: !!ownProps.routeParams.id && !!ownProps.viewer.node,
      userIsLoggedIn: isLoggedIn,
      userProfile: profile,
    };
  },
  (dispatch) => ({
    dispatch,
    setPlayerQueue: (tracks: IPlayerTrack[]) => {
      dispatch(playerActions.replaceQueue({ tracks }));
    },
  }),
  (stateProps, { dispatch, ...dispatchProps }, ownProps) => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    reinitializeForm: (formFields) => {
      dispatch(reinitializeForm(formFields));
    },
  }),
  {
    areStatesEqual: (nextState, prevState) => {
      return isEqual(nextState.user, prevState.user);
    },
  },
)(ProjectsPageForm);

const RelayProjectsPage = createFragmentContainer(
  ConnectedProjectsPageForm,
  ProjectPageFragment,
);

export default RelayProjectsPage;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/pages/Projects/index.tsx